import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, CheckCircle2 } from "lucide-react";
import { SOLUTIONS, COMPANY } from "../data/content";
import { Reveal, SectionTag, PageHeader } from "../components/Shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL = { name: "", company: "", email: "", phone: "", product_type: "", dimensions: "", quantity: "", timeline: "", details: "" };
const TIMELINES = ["ASAP / Rush", "Within 2 weeks", "Within a month", "1–3 months", "Just exploring"];

export default function Quote() {
  const [form, setForm] = useState(INITIAL);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/quote`, form);
      toast.success("Quote request received! Our team will reply within one business day.");
      setDone(true);
    } catch {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="quote-page">
      <PageHeader
        tag="Request a Quote"
        title={<>Tell Us the Specs.<br />We'll Do the Rest.</>}
        subtitle="Share your project requirements below — dimensions, materials, quantities, timelines — and we'll respond with pricing and recommendations within one business day."
      />

      <section className="pb-28">
        <div className="msg-container grid grid-cols-1 lg:grid-cols-3 gap-16">
          <Reveal className="lg:col-span-2">
            {done ? (
              <div className="msg-card p-12 text-center" data-testid="quote-success">
                <CheckCircle2 size={56} className="mx-auto" style={{ color: "var(--brand-lime)" }} />
                <h2 className="font-display font-black uppercase text-2xl mt-6">Request Received</h2>
                <p className="mt-4 text-base md:text-lg" style={{ color: "var(--text-2)" }}>
                  Thanks {form.name.split(" ")[0]}! Your quote request is with our estimating team.
                  Expect a reply at <strong>{form.email}</strong> within one business day.
                </p>
                <button className="btn-ghost mt-8" onClick={() => { setForm(INITIAL); setDone(false); }} data-testid="quote-another-btn">
                  Submit Another Request
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="msg-card p-8 lg:p-10" data-testid="quote-form">
                <SectionTag>Project Details</SectionTag>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-8">
                  <div>
                    <label className="msg-label">Full Name *</label>
                    <input className="msg-input" required value={form.name} onChange={set("name")} placeholder="Jane Smith" data-testid="quote-name-input" />
                  </div>
                  <div>
                    <label className="msg-label">Company</label>
                    <input className="msg-input" value={form.company} onChange={set("company")} placeholder="Company Inc." data-testid="quote-company-input" />
                  </div>
                  <div>
                    <label className="msg-label">Email *</label>
                    <input className="msg-input" type="email" required value={form.email} onChange={set("email")} placeholder="jane@company.com" data-testid="quote-email-input" />
                  </div>
                  <div>
                    <label className="msg-label">Phone *</label>
                    <input className="msg-input" required value={form.phone} onChange={set("phone")} placeholder="647-000-0000" data-testid="quote-phone-input" />
                  </div>
                  <div>
                    <label className="msg-label">Product / Solution *</label>
                    <select className="msg-select" required value={form.product_type} onChange={set("product_type")} data-testid="quote-product-select">
                      <option value="" disabled>Select a product…</option>
                      {SOLUTIONS.map((s) => <option key={s.id} value={s.title}>{s.title}</option>)}
                      <option value="Custom / Other">Custom / Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="msg-label">Timeline</label>
                    <select className="msg-select" value={form.timeline} onChange={set("timeline")} data-testid="quote-timeline-select">
                      <option value="" disabled>Select timeline…</option>
                      {TIMELINES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="msg-label">Approx. Dimensions</label>
                    <input className="msg-input" value={form.dimensions} onChange={set("dimensions")} placeholder={"e.g. 4 ft × 8 ft"} data-testid="quote-dimensions-input" />
                  </div>
                  <div>
                    <label className="msg-label">Quantity</label>
                    <input className="msg-input" value={form.quantity} onChange={set("quantity")} placeholder="e.g. 25 units" data-testid="quote-quantity-input" />
                  </div>
                </div>
                <div className="mt-6">
                  <label className="msg-label">Project Details</label>
                  <textarea className="msg-textarea" rows={5} value={form.details} onChange={set("details")} placeholder="Materials, lighting, install location, brand guidelines…" data-testid="quote-details-input" />
                </div>
                <button type="submit" className="btn-brand mt-8" disabled={sending} data-testid="quote-submit-btn">
                  {sending ? "Submitting…" : "Submit Quote Request"} <Send size={15} />
                </button>
              </form>
            )}
          </Reveal>

          <Reveal delay={0.12}>
            <div className="space-y-6">
              <div className="msg-card p-8 relative overflow-hidden">
                <span className="corner-notch" />
                <h3 className="font-display font-bold uppercase text-lg">What Happens Next</h3>
                <ol className="mt-6 space-y-5">
                  {["We review your specs within one business day", "You receive pricing, materials & timeline options", "Approve — and production begins in Woodbridge"].map((s, i) => (
                    <li key={s} className="flex gap-4 text-sm leading-relaxed" style={{ color: "var(--text-2)" }}>
                      <span className="font-display font-black text-xl shrink-0" style={{ color: ["var(--brand-orange)", "var(--brand-magenta)", "var(--brand-cyan)"][i] }}>{i + 1}</span>
                      {s}
                    </li>
                  ))}
                </ol>
              </div>
              <div className="p-8" style={{ background: "var(--bg-2)", border: "1px solid var(--line)" }}>
                <p className="font-mono text-[10px] tracking-[0.2em] uppercase" style={{ color: "var(--text-3)" }}>Prefer to talk?</p>
                <a href={`tel:${COMPANY.phoneRaw}`} className="font-display font-black text-2xl mt-2 block hover:text-[var(--brand-cyan)] transition-colors" data-testid="quote-phone-link">{COMPANY.phone}</a>
                <a href={`mailto:${COMPANY.email}`} className="text-sm mt-1 block hover:text-[var(--brand-cyan)] transition-colors" style={{ color: "var(--text-2)" }} data-testid="quote-email-link">{COMPANY.email}</a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
